import { posthog } from "posthog-js";
import { isProdMode } from "@base/utils/isProdMode";
import * as Sentry from "@sentry/gatsby";
import { TrackingEvent as QuizTrackingEvent } from "@base/lib/quiz-lib/public/types";
import { eventToGtmEvent, GtmEvent } from "./gtm";
import { TrackablePurchaseType } from "@base/services/coreapi";
import { refreshWebSessionOnReturn, upsertWebSession } from "@base/services/webSession";
import { getAnonUserId } from "@base/utils/userID";
import { getCookie, setCookie } from "@base/utils/cookie";
import { v4 as uuidv4 } from "uuid";
import { AdChannel, getAdChannels } from "./channels";
import { isTrackerOn } from "./activeTrackers";
import { logTrackingDebug } from "./debug/debugLog";
import { CHECKOUT_TOKEN_HEADER } from "@base/lib/checkoutSession";

type CommonPurchaseProperties = {
  value: number;
  currency: string;
  productID: string;
  orderID: string;
  konnektiveOrderID?: string;
  purchaseProductType: TrackablePurchaseType;
  campaignProductID?: string;
  readableID: string;
  productName: string;
  paymentType?: string;
  originalAmount?: number;
  discountAmount?: number;
  discountPercent?: number;
  numberOfDiscounts?: number;
  upsellIdx?: number;
};

type AppTrackingEventPurchaseFailed = {
  name: "purchase-failed";
  properties: {
    value: number;
    currency: string;
    productID: string;
    readableID: string;
    productName: string;
    paymentType?: string;
    upsellIdx?: number;
    purchaseProductType: TrackablePurchaseType;
    errorMessage: string;
    // Provider-supplied context (e.g. CCBill stage / decline code / raw error)
    [key: string]: any;
  };
};

/**
 * Fired for every payment failure the user actually sees — pairs what we
 * DISPLAYED (mapped banner copy) with what we RECEIVED from the server, so
 * unmapped decline messages can be mined and added to declineMessages.ts.
 */
type AppTrackingEventPaymentErrorDisplayed = {
  name: "payment-error-displayed";
  properties: {
    surface: "checkout" | "upsell";
    displayedKind: string;
    displayedHeadline: string;
    displayedHint?: string;
    receivedMessage: string;
    declineCode?: string | number | null;
    serviceUnreachable?: boolean;
    /** True when we fell back to generic copy — these are the ones to mine. */
    unmapped: boolean;
    provider?: string;
    stage?: string;
    paymentType?: string;
    productID?: string;
    rawDetails?: string;
  };
};

type AppTrackingEventAddPaymentInfo = {
  name: "add-payment-info";
  properties: CommonPurchaseProperties;
};

type AppTrackingEventPurchase = {
  name: "purchase";
  properties: CommonPurchaseProperties;
};

/** Fired by the shared EmailInput (email-lib) assist features. */
type AppTrackingEventEmailAssist = {
  name:
    | "email-typo-suggestion-shown"
    | "email-typo-suggestion-accepted"
    | "email-typo-suggestion-blocked-submit"
    | "email-typo-suggestion-overridden"
    | "email-domain-autocomplete-accepted";
  properties: {
    /** Which surface the input was rendered on, e.g. "quiz". */
    context: string;
    suggestion: string;
  };
};

type AppTrackingEvent =
  | AppTrackingEventEmailAssist
  | AppTrackingEventAddPaymentInfo
  | AppTrackingEventPurchase
  | AppTrackingEventPurchaseFailed
  | AppTrackingEventPaymentErrorDisplayed
  | {
      name: "tried-to-register-with-existing-email";
      properties: Record<string, any>;
    }
  | {
      name: "email-form-opened";
      properties: Record<string, any>;
    }
  | {
      name: "email-form-submitted";
      properties: Record<string, any>;
    }
  | {
      name: "lead-created";
      properties: {
        email: string;
      };
    }
  | {
      name: "quiz-finished-with-email";
      properties: {
        email: string;
      };
    }
  | {
      /** Every Gatsby route update, including the initial pageload. */
      name: "page-view";
      properties: {
        path: string;
      };
    }
  | {
      name: "to-pricing-button-clicked";
      properties: Record<string, any>;
    }
  | {
      /** A plan was picked on the pricing section; user is heading to checkout. */
      name: "plan-selected";
      properties: {
        value: number;
        currency: string;
        productID: string;
      };
    }
  | {
      name: "downsell-opened";
      properties: Record<string, any>;
    }
  | {
      name: "downsell-accepted";
      properties: Record<string, any>;
    }
  | {
      name: "downsell-declined";
      properties: Record<string, any>;
    }
  | {
      name: "upsell-skipped";
      properties: Record<string, any>;
    }
  | {
      name: "page_view_summary";
      properties: Record<string, any>;
    }
  | {
      name: "page_view_checkout";
      properties: {
        productID: string;
        readableID: string;
        campaignProductID?: string;
        productName: string;
        value: number;
        currency: string;
        originalAmount?: number;
        discountAmount?: number;
        discountPercent?: number;
        numberOfDiscounts?: number;
      };
    }
  | {
      name: `page_view_upsell_${number}`;
      properties: {};
    }
  | {
      name: "apple-pay-name-modal-opened";
      properties: {};
    }
  | {
      name: "ebook-download-started";
      properties: {
        slug: string;
        title: string;
        fileName: string;
        variant?: string;
      };
    };

export type TrackingEvent = QuizTrackingEvent | AppTrackingEvent;

export function trackEvent(e: TrackingEvent) {
  logTrackingDebug(null, e.name, e.properties);

  const eventId = uuidv4();
  forEachAdChannel("track", (channel) => channel.track(e, eventId));

  if (e.name === "page-view") {
    // GTM is skipped: it gets `route_change` from gatsby-plugin-google-tagmanager.
    capturePostHog("$pageview");
    return;
  }

  trackGtmEvent(eventToGtmEvent(e));

  // PostHog automatically includes registered global properties
  capturePostHog(e.name, e.properties);
}

function capturePostHog(name: string, properties?: Record<string, any>) {
  posthog.capture(name, properties);
  if (isTrackerOn("posthog")) {
    logTrackingDebug("posthog", name);
  }
}

/**
 * Vendor scripts are third-party code, and trackEvent runs inside checkout and
 * payment flows: one channel throwing must never break the caller or the other
 * channels.
 */
function forEachAdChannel(action: string, fn: (channel: AdChannel) => void) {
  for (const channel of getAdChannels()) {
    try {
      fn(channel);
    } catch (error) {
      logTrackingDebug(channel.name, `ERROR in ${action}`, String(error));
      captureError(error, { context: "ad-channel", channel: channel.name, action });
    }
  }
}

/**
 * Send a handled error to PostHog error tracking (and Sentry).
 * Unhandled errors and promise rejections are captured automatically,
 * use this for errors you catch yourself:
 *
 *   try { ... } catch (e) { captureError(e, { context: "checkout" }) }
 */
export function captureError(error: unknown, properties?: Record<string, any>) {
  posthog.captureException(error, properties);
  Sentry.captureException(error, { extra: properties });
}

type LogAttributes = Record<string, any>;

/**
 * Structured logging that is sent to PostHog Logs
 * (linked to the current person and session replay).
 * Also writes to the console, so logs are picked up by Sentry
 * and visible during local development.
 */
export const logger = {
  debug(message: string, attributes?: LogAttributes) {
    console.debug(message, attributes ?? "");
    posthog.logger.debug(message, attributes);
  },
  info(message: string, attributes?: LogAttributes) {
    console.info(message, attributes ?? "");
    posthog.logger.info(message, attributes);
  },
  warn(message: string, attributes?: LogAttributes) {
    console.warn(message, attributes ?? "");
    posthog.logger.warn(message, attributes);
  },
  error(message: string, attributes?: LogAttributes) {
    console.error(message, attributes ?? "");
    posthog.logger.error(message, attributes);
  },
};

/**
 * Give every ad channel the user's email and our user ID for advanced matching.
 * Call as soon as we know them (lead capture, or a restored user on checkout).
 */
export function identifyAdUser(user: { email: string; userID?: string }) {
  forEachAdChannel("identify", (channel) => channel.identify(user));
}

export function initSentry(dsn: string, feVersion: string) {
  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({
        levels: ["error", "log"],
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    release: feVersion,
    environment: isProdMode() ? "production" : "development",
    enableLogs: true,
  });

  Sentry.setUser({
    id: getAnonUserId(),
  });
}

// Global context that gets mapped to different tracking services
interface GlobalTrackingContext {
  appName: string;
  appVersion: string;
  brand: string;
  abTestName?: string;
  abTestValue?: string;
  environment?: string;
  funnelID?: string;
}

let globalContext: Partial<GlobalTrackingContext> = {};

/**
 * Set global tracking context that will be automatically included in all tracking events
 */
export function setGlobalTrackingContext(
  context: Partial<GlobalTrackingContext>,
) {
  // first let's set the context
  globalContext = { ...globalContext, ...context };

  // then set context for each service
  posthog.register(getGlobalPostHogContext());
  if ((window as any).dataLayer) {
    (window as any).dataLayer.push({
      context: getGlobalGtmContext(),
    });
  }
}

function getGlobalGtmContext() {
  // Add AB test info to GTM context in a specific format
  // this format is defined by the marketing team
  let affid = "";
  if (globalContext.abTestName && globalContext.abTestValue) {
    affid = `${globalContext.abTestName}+${globalContext.abTestValue}`;
  }

  return {
    app_name: globalContext.appName,
    app_version: globalContext.appVersion,
    brand: globalContext.brand,
    environment: globalContext.environment,
    affid,
    funnel_id: globalContext.funnelID,
    ...getMarketingParamsFromLocalStorage(),
  };
}

function getGlobalPostHogContext() {
  return { ...globalContext };
}

// Configuration interface for tracking initialization
interface TrackingConfig {
  posthog?: {
    token: string;
    apiHost: string;
    enabled?: boolean;
  };
  gtm?: {
    // GTM is initialized via gatsby-plugin-google-tagmanager
    // This section is for future GTM-specific config if needed
    ga4Config?: {
      server_container_url?: string;
    };
  };
  globalContext?: Partial<GlobalTrackingContext>;
}

/**
 * Initialize all tracking services with unified configuration
 * Call this once from gatsby-browser.tsx
 */
export function initTracking(config: TrackingConfig) {
  // initialise our own session tracking
  // do not await, if it fails it's not critical; the service owns url/sid
  // decoration and persistence, so this early call is safe on its own
  upsertWebSession().catch((err) => {
    console.error("Failed to upsert web session:", err);
  });
  refreshWebSessionOnReturn();

  // Initialize PostHog if configured and enabled
  if (config.posthog?.enabled) {
    const featureFlags: Record<string, string> = {};
    if (config.globalContext?.abTestName && config.globalContext.abTestValue) {
      featureFlags[config.globalContext.abTestName] =
        config.globalContext.abTestValue;
    }

    posthog.init(config.posthog.token, {
      api_host: config.posthog.apiHost,
      capture_pageview: false,
      autocapture: false,
      debug: false,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
      logs: {
        serviceName: config.globalContext?.appName,
        serviceVersion: config.globalContext?.appVersion,
        environment: config.globalContext?.environment,
      },
      disable_session_recording: !isProdMode(),
      advanced_disable_feature_flags_on_first_load: true,
      advanced_disable_feature_flags: true,
      cross_subdomain_cookie: true,
      session_recording: {
        // Recordings must never hold the checkout token, or /checkout/* bodies
        // (jump tokens, emails).
        maskCapturedNetworkRequestFn: (request) => {
          const headers = request.requestHeaders;
          if (headers) {
            for (const key of Object.keys(headers)) {
              if (key.toLowerCase() === CHECKOUT_TOKEN_HEADER) delete headers[key];
            }
          }
          if (request.name.includes("/checkout/")) {
            request.requestBody = null;
            request.responseBody = null;
          }
          return request;
        },
      },
      bootstrap: {
        featureFlags,
        distinctID: getAnonUserId(),
      },
    });
  }

  // Set initial global context
  const defaultContext: Partial<GlobalTrackingContext> = {};

  setGlobalTrackingContext({
    ...defaultContext,
    ...config.globalContext,
  });

  // custom event to initialize GA4 via GTM
  trackGtmEvent({
    event: "init_ga4",
    eventData: {
      user_id: getAnonUserId(),
      ...config.gtm?.ga4Config,
    },
  });
}

function trackGtmEvent(e: GtmEvent) {
  if (typeof window === "undefined") {
    return;
  }

  if (!(window as any).dataLayer) {
    return;
  }

  (window as any).dataLayer.push({
    ...e,
  });
  logTrackingDebug("gtm", e.event, e.eventData);
}

function getMarketingParamsFromUrl() {
  const capturableParamNames = [
    "fbclid",
    "ad_id",
    "site_id",
    "creative_id",
    "creative_name",
  ];

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const marketingParams: Record<string, string> = {};

  params.forEach((value, key) => {
    if (key.startsWith("utm_")) {
      marketingParams[key] = value;
    }

    if (capturableParamNames.includes(key)) {
      marketingParams[key] = value;
    }
  });

  return marketingParams;
}

const MARKETING_PARAMS_KEY = "marketing_params_last_touch";
const MARKETING_PARAMS_COOKIE = "mp_lt";

export function saveMarketingParamsToLocalStorage() {
  const params = getMarketingParamsFromUrl();
  const existing = getMarketingParamsFromLocalStorage();
  const merged = { ...existing, ...params };

  const json = JSON.stringify(merged);
  localStorage.setItem(MARKETING_PARAMS_KEY, json);
  // Mirror to cookie so data survives Safari ITP localStorage wipes after cross-site redirects
  setCookie(MARKETING_PARAMS_COOKIE, json);
}

export function getMarketingParamsFromLocalStorage(): Record<string, string> {
  // Try localStorage first, fall back to cookie (Safari ITP protection)
  const item = localStorage.getItem(MARKETING_PARAMS_KEY);
  const raw = item || getCookie(MARKETING_PARAMS_COOKIE);

  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      // If we recovered from cookie, re-populate localStorage
      if (!item && parsed) {
        localStorage.setItem(MARKETING_PARAMS_KEY, JSON.stringify(parsed));
      }
      return parsed;
    } catch (e) {
      console.error("Failed to parse marketing_params_last_touch", e);
      return {};
    }
  }
  return {};
}

export function getUtmParamsFromLocalStorage(): Record<string, string> {
  const marketingParams = getMarketingParamsFromLocalStorage();
  const utmParams: Record<string, string> = {};
  Object.keys(marketingParams).forEach((key) => {
    if (key.startsWith("utm_")) {
      utmParams[key] = marketingParams[key];
    }
  });
  return utmParams;
}
